import React, { useEffect, useState } from "react";

import {
    Autorenew as ProcessingIcon,
    CheckCircle as ReadyIcon,
    CloudUpload as UploadIcon,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import { getMetaCustomAudienceStats } from "../../helpers/destinations/createDestination";

type StageConfig = {
    label: string;
    color: "info" | "primary" | "success" | "default";
    icon: React.ReactElement;
};

const STAGE_MAP: Record<string, StageConfig> = {
    uploading: {
        label: "Uploading",
        color: "info",
        icon: <UploadIcon fontSize="small" />,
    },
    processing: {
        label: "Processing",
        color: "primary",
        icon: <ProcessingIcon fontSize="small" />,
    },
    ready: {
        label: "Ready",
        color: "success",
        icon: <ReadyIcon fontSize="small" />,
    },
};

const getStageChip = (stage: string | undefined) => {
    const normalized = stage?.toLowerCase().trim();
    const config = normalized ? STAGE_MAP[normalized] : undefined;

    if (!config) {
        return (
            <Chip
                label={
                    stage
                        ? stage.charAt(0).toUpperCase() + stage.slice(1)
                        : "Unknown"
                }
                size="small"
                variant="outlined"
                onClick={() => {}}
            />
        );
    }

    return (
        <Chip
            icon={config.icon}
            label={config.label}
            color={config.color}
            size="small"
            variant="outlined"
            onClick={() => {}}
        />
    );
};

const PLATFORM_LABELS: Record<
    string,
    { title: string; statsTitle: string; description: string }
> = {
    fbca: {
        title: "Meta Custom Audience",
        statsTitle: "Meta Custom Audience Session Stats",
        description:
            "This section provides detailed insights into the latest audience upload sessions for your Meta Custom Audiences. It includes status updates, data matching progress, retention duration, and operational readiness — helping you monitor and optimize audience delivery effectively.",
    },
    ttca: {
        title: "TikTok Custom Audience",
        statsTitle: "TikTok Custom Audience Session Stats",
        description:
            "This section provides detailed insights into the latest audience upload sessions for your TikTok Custom Audiences. It includes status updates, data matching progress, retention duration, and operational readiness — helping you monitor and optimize audience delivery effectively.",
    },
};

export const getCustomAudienceOverview = ({
    isLoading,
    data,
    destination,
    platformCode,
}: {
    isLoading: boolean;
    data: any;
    destination: any;
    platformCode: string;
}) => {
    const hasSessions =
        Array.isArray(data?.sessions) && data.sessions.length > 0;
    const labels = PLATFORM_LABELS[platformCode] || PLATFORM_LABELS.fbca;

    return (
        <Stack spacing={2}>
            {destination &&
                (destination?.name || destination?.partner_reference_id) && (
                    <Box>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            {labels.title}
                        </Typography>
                        <Stack spacing={1}>
                            {destination?.name && (
                                <Typography variant="body2">
                                    <strong>Name:</strong> {destination.name}
                                </Typography>
                            )}
                            {destination?.partner_reference_id && (
                                <Typography variant="body2">
                                    <strong>ID:</strong>{" "}
                                    {destination.partner_reference_id}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                )}

            <Typography variant="h6" fontWeight="bold">
                {labels.statsTitle}
            </Typography>

            <Typography variant="body1">{labels.description}</Typography>

            {data?.last_updated_at && (
                <Typography variant="body2">
                    <strong>Last updated on:</strong> {data.last_updated_at}
                </Typography>
            )}

            {data?.audience_range?.label && (
                <Typography variant="body2">
                    <strong>Approximate Audience Count:</strong>{" "}
                    {data.audience_range.label}
                </Typography>
            )}

            {data?.audience_status?.delivery?.description && (
                <Typography variant="body2">
                    <strong>Delivery Status:</strong>{" "}
                    {data.audience_status.delivery.description}
                </Typography>
            )}

            {data?.retention_days != null && (
                <Typography variant="body2">
                    <strong>Retention Days:</strong>{" "}
                    {data.retention_days === 0
                        ? "Forever"
                        : `${data.retention_days} days`}
                </Typography>
            )}

            <Box>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : hasSessions ? (
                    <TableContainer component={Paper}>
                        <Table
                            sx={{ minWidth: 750 }}
                            aria-label="session table"
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>Session ID</TableCell>
                                    <TableCell align="left">Date</TableCell>
                                    <TableCell align="left">
                                        Received Count
                                    </TableCell>
                                    <TableCell align="center">Stage</TableCell>
                                    <TableCell align="left">
                                        Matched Count
                                    </TableCell>
                                    <TableCell align="left">
                                        Retention Days
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.sessions.map((row) => (
                                    <TableRow
                                        key={row.session_id}
                                        sx={{
                                            "&:last-child td, &:last-child th":
                                                {
                                                    border: 0,
                                                },
                                        }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {row.session_id}
                                        </TableCell>
                                        <TableCell align="left">
                                            {row.started_at}
                                        </TableCell>
                                        <TableCell align="left">
                                            {row.received_count}
                                        </TableCell>
                                        <TableCell align="center">
                                            {getStageChip(row.stage)}
                                        </TableCell>
                                        <TableCell align="left">
                                            {row.matched_count != null &&
                                            row.matched_count >= 0
                                                ? row.matched_count
                                                : "—"}
                                        </TableCell>
                                        <TableCell align="left">
                                            {data?.retention_days != null
                                                ? data.retention_days === 0
                                                    ? "Forever"
                                                    : `${data.retention_days}`
                                                : "—"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        No sessions found.
                    </Alert>
                )}
            </Box>
        </Stack>
    );
};

export const getMetaCustomAudienceOverview = ({
    isLoading,
    data,
    destination,
}) => {
    return getCustomAudienceOverview({
        isLoading,
        data,
        destination,
        platformCode: "fbca",
    });
};

const STATS_FETCHERS: Record<string, (id: number) => Promise<any>> = {
    fbca: getMetaCustomAudienceStats,
};

export const CustomAudienceOverview = ({ destination }) => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const code = destination?.destination_type?.code;
    const fetcher = code ? STATS_FETCHERS[code] : undefined;

    useEffect(() => {
        if (!fetcher || !destination?.id) return;

        let cancelled = false;
        const fetchStats = async () => {
            setIsLoading(true);
            try {
                const response = await fetcher(destination.id);
                if (!cancelled) setData(response.data?.session_stat || null);
            } catch (error) {
                console.error(
                    `Error fetching ${code} Custom Audience stats:`,
                    error,
                );
                if (!cancelled) setData(null);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        fetchStats();
        return () => {
            cancelled = true;
        };
    }, [destination?.id, code, fetcher]);

    return getCustomAudienceOverview({
        isLoading,
        data,
        destination,
        platformCode: code || "fbca",
    });
};

export const MetaCustomAudienceOverview = ({ destination }) => {
    return <CustomAudienceOverview destination={destination} />;
};
